import type { UsageView } from "@crux/contracts";

/**
 * How long each screen was on screen, for the operator panel's Usage page.
 *
 * A view starts when a screen is shown in a visible tab and ends when the
 * person moves to another screen, the tab is hidden or closed, the workspace
 * changes, or they sign out. Hidden time is not counted: a tab left in the
 * background all afternoon is not somebody reading it. A view ends only at
 * one of those moments, never on its own, so what is sent is always a view
 * that really ended.
 *
 * Every view is sent, however short: a screen passed through on the way to
 * another one is part of how the app is used, and filtering it here would
 * decide for the reader what counts.
 */

/** A gap this long starts a new visit, as the common analytics tools count one. */
export const VISIT_IDLE_MS = 30 * 60 * 1000;

/** The server's ceiling (`usageViewSchema`): longer is a clock gone wrong. */
const MOST_ACTIVE_MS = 21_600_000;

/** The visit in progress: its id, and when anything last happened in it (epoch ms). */
export interface Visit {
  readonly id: string;
  readonly lastActive: number;
}

export interface ScreenClockDeps {
  /** Wall-clock milliseconds: a visit outlives the page, so it needs a clock that does too. */
  readonly now: () => number;
  readonly newVisitId: () => string;
  /** Hands the ended views of one workspace to the network. */
  readonly send: (workspaceId: string, views: readonly UsageView[]) => Promise<void>;
  readonly release: string | undefined;
  /**
   * Where the visit is kept between pages: a reload, or a second tab opened a
   * minute later, is the same visit, as the common analytics tools count one.
   */
  readonly loadVisit: () => Visit | null;
  readonly saveVisit: (visit: Visit) => void;
}

interface Showing {
  readonly workspaceId: string;
  readonly screen: string;
  /** When it last became visible; null while the tab is hidden. */
  readonly since: number | null;
}

export interface ScreenClock {
  /** The screen on show now. The same screen again changes nothing. */
  readonly show: (workspaceId: string, screen: string, visible: boolean) => void;
  /** The tab was hidden or is closing: the view ends, the screen stays. */
  readonly hide: () => void;
  /** The tab is visible again: the same screen starts a new view. */
  readonly reveal: () => void;
  /**
   * Signing out, or no workspace to show: the view ends and nothing follows.
   * Settles when the view has been sent, so a sign-out can wait for it.
   */
  readonly stop: () => Promise<void>;
  /** The shell went away without saying why: drop the view, send nothing. */
  readonly forget: () => void;
}

export function createScreenClock(deps: ScreenClockDeps): ScreenClock {
  let showing: Showing | null = null;
  /** The visit a view that began at `start` and ended at `end` belongs to. */
  const visitId = (start: number, end: number): string => {
    const kept = deps.loadVisit();
    const id =
      kept === null || start - kept.lastActive > VISIT_IDLE_MS
        ? deps.newVisitId()
        : kept.id;
    deps.saveVisit({ id, lastActive: end });
    return id;
  };

  const end = (): Promise<void> => {
    const current = showing;
    if (current?.since == null) return Promise.resolve();
    const now = deps.now();
    const view: UsageView = {
      screen: current.screen,
      visitId: visitId(current.since, now),
      activeMs: Math.min(Math.max(0, Math.round(now - current.since)), MOST_ACTIVE_MS),
      ...(deps.release === undefined ? {} : { release: deps.release }),
    };
    showing = { ...current, since: null };
    return deps.send(current.workspaceId, [view]);
  };

  return {
    show: (workspaceId, screen, visible) => {
      if (showing?.workspaceId === workspaceId && showing.screen === screen) return;
      void end();
      showing = { workspaceId, screen, since: visible ? deps.now() : null };
    },
    hide: () => {
      void end();
    },
    reveal: () => {
      // Nothing on show, or already counting: there is nothing to start.
      if (showing?.since !== null) return;
      showing = { ...showing, since: deps.now() };
    },
    stop: () => {
      const sent = end();
      showing = null;
      return sent;
    },
    forget: () => {
      showing = null;
    },
  };
}
